import { useState, useRef, useEffect } from "react";
import { BRAND, GradText, Section } from "../brand";

const DEMO_QUESTIONS = [
  {
    question: "Who gets priority — the party of 2 at the host stand, or the 15-top on hold?",
    insight:
      "Hosts pick the table they can see. The $400 catering inquiry hangs up silently.",
  },
  {
    question: "Are you getting calls outside business hours?",
    insight:
      "30–40% of restaurant calls land outside service. Right now they go to voicemail — and 62% never leave one.",
  },
  {
    question: "How do you handle catering inquiries today?",
    insight:
      "Callbacks lose deals. By the time you call back, the planner has already booked elsewhere.",
  },
  {
    question: "What happens on a Friday night when the floor is full and the phone is ringing?",
    insight:
      "Whichever host is closest picks up — distracted, half-listening, back to the floor in 30 seconds.",
  },
];

export default function PainPointsSlide({ proposalData, isMobile, isDemo }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const painPoints = proposalData.painPoints || [];

  return (
    <Section innerRef={ref} style={{ background: BRAND.white }}>
      <div style={{ maxWidth: 880, margin: "0 auto", width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 28 : 40 }}>
          <div
            style={{
              fontSize: 13,
              letterSpacing: 3,
              color: BRAND.orange,
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            {isDemo ? "Questions worth asking" : "What we heard"}
          </div>
          <h2
            style={{
              fontSize: isMobile ? 28 : 44,
              fontWeight: 800,
              color: BRAND.dark,
              margin: 0,
              lineHeight: 1.1,
              fontFamily: "'SF Pro Rounded', 'Manrope', sans-serif",
            }}
          >
            {isDemo ? (
              <>
                The conversations every operator{" "}
                <GradText
                  style={{
                    fontSize: isMobile ? 28 : 44,
                    fontWeight: 800,
                    fontFamily: "'SF Pro Rounded', 'Manrope', sans-serif",
                  }}
                >
                  avoids.
                </GradText>
              </>
            ) : (
              <>
                Your challenges,{" "}
                <GradText
                  style={{
                    fontSize: isMobile ? 28 : 44,
                    fontWeight: 800,
                    fontFamily: "'SF Pro Rounded', 'Manrope', sans-serif",
                  }}
                >
                  our solutions
                </GradText>
              </>
            )}
          </h2>
          {!isDemo && (
            <p
              style={{
                color: "#64748B",
                fontSize: isMobile ? 15 : 17,
                marginTop: 12,
                maxWidth: 580,
                marginLeft: "auto",
                marginRight: "auto",
                lineHeight: 1.6,
              }}
            >
              Here's what we identified as the biggest opportunities for{" "}
              {proposalData.clientName || "your restaurant"}.
            </p>
          )}
        </div>

        {isDemo ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            {DEMO_QUESTIONS.map((q, i) => (
              <div
                key={i}
                style={{
                  position: "relative",
                  background: BRAND.white,
                  borderRadius: 16,
                  border: "1px solid #EEF2F6",
                  padding: isMobile ? "24px 22px 24px 26px" : "28px 28px 28px 32px",
                  overflow: "hidden",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(20px)",
                  transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: BRAND.gradient,
                  }}
                />
                <div
                  style={{
                    fontSize: isMobile ? 17 : 19,
                    fontWeight: 700,
                    color: BRAND.dark,
                    lineHeight: 1.3,
                    marginBottom: 10,
                    fontFamily: "'SF Pro Rounded', 'Manrope', sans-serif",
                  }}
                >
                  {q.question}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: isMobile ? 13.5 : 14.5,
                    color: "#64748B",
                    lineHeight: 1.55,
                  }}
                >
                  {q.insight}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
            }}
          >
            {painPoints.map((p, i) => (
              <div
                key={i}
                style={{
                  borderRadius: 20,
                  overflow: "hidden",
                  border: "1px solid #EEF2F6",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(20px)",
                  transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`,
                }}
              >
                <div
                  style={{
                    background: BRAND.dark,
                    padding: "20px 22px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: BRAND.orange,
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    Challenge
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      color: BRAND.white,
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {p.pain}
                  </div>
                </div>
                <div style={{ background: "#FAFAFA", padding: "20px 22px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#22C55E",
                          textTransform: "uppercase",
                          letterSpacing: 1.5,
                          fontWeight: 600,
                          marginBottom: 4,
                        }}
                      >
                        RestoHost Solution
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          color: "#475569",
                          lineHeight: 1.5,
                        }}
                      >
                        {p.impact}
                      </div>
                    </div>
                    {p.metric && (
                      <div
                        style={{
                          textAlign: "center",
                          flexShrink: 0,
                          paddingLeft: 12,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 800,
                            background: BRAND.gradient,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {p.metric}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#94A3B8",
                            lineHeight: 1.2,
                          }}
                        >
                          {p.metricLabel}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            marginTop: 28,
            padding: isMobile ? "16px 20px" : "20px 28px",
            borderRadius: 16,
            background: BRAND.dark,
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: 0,
              color: BRAND.white,
              fontSize: isMobile ? 15 : 18,
              fontWeight: 600,
            }}
          >
            {isDemo ? (
              <>
                Sound familiar?{" "}
                <GradText style={{ fontWeight: 700, fontSize: isMobile ? 15 : 18 }}>
                  Here's what RestoHost does about it. ↓
                </GradText>
              </>
            ) : (
              <>
                We built this proposal around{" "}
                <em style={{ color: BRAND.orange, fontStyle: "normal" }}>
                  your specific needs
                </em>
                .{" "}
                <GradText style={{ fontWeight: 700, fontSize: isMobile ? 15 : 18 }}>
                  Let's make it happen.
                </GradText>
              </>
            )}
          </p>
        </div>
      </div>
    </Section>
  );
}
